import { Component, ElementRef, Input, Optional, Self, ViewChild } from '@angular/core';
import { BlockLifeAct } from '@src/app/@shared/models/step.model';
import { AbstractControl, FormBuilder, FormControl, NgControl } from '@angular/forms';
import { BlockAbstractComponent } from '@app/components/blocks/block.abstract.component';
import { LifeActValue } from '@app/components/lifeAct/lifeAct.component';
import { ApiUrlProgramData, CourseContentService, ProgramDataCode } from '@app/courseContent/courseContent.service';
import { TimeFormat } from '@app/components/pl-timepicker/pl-timepicker.component';

export enum FormControlName {
  lifeact = 'lifeact',
}

interface LifeActForm {
  [FormControlName.lifeact]: FormControl<LifeActValue>;
}

@Component({
  selector: 'pl-block-lifeact',
  templateUrl: './block-lifeact.component.html',
})
export class BlockLifeactComponent extends BlockAbstractComponent<BlockLifeAct, LifeActForm> {
  @Input({ required: true }) public timeFormat!: TimeFormat;

  protected FormControlName: typeof FormControlName = FormControlName;

  @ViewChild('plInput', { read: ElementRef }) public plInputElement!: ElementRef;

  constructor(
    @Optional() @Self() public override controlDir: NgControl,
    protected override courseContentService: CourseContentService,
  ) {
    super(controlDir, courseContentService);

    this.hostBindingClasses.update((hostBindingClasses: Set<string>) => hostBindingClasses.add('outline-none'));
  }

  protected override initForm(): void {
    const fb: FormBuilder = new FormBuilder();
    this.form = fb.nonNullable.group<LifeActForm>({
      lifeact: fb.nonNullable.control({
        note: null,
        completed: false,
      }),
    });
  }

  protected override patchFormWithEmptyValue(): void {
    if (this.form) {
      this.form.patchValue(
        {
          lifeact: {
            note: null,
            completed: false,
          },
        },
        { emitEvent: false },
      );
    } else {
      throw new Error(`patchFormWithEmptyValue() this.form should not be null when this function is called`);
    }
  }

  protected override patchFormFromBlock(block: BlockLifeAct): void {
    if (this.form) {
      this.form.patchValue(
        {
          lifeact: {
            note: block.data.value || '',
            completed: !!block.data.lifeActCompleted,
          },
        },
        { emitEvent: false },
      );
    } else {
      throw new Error(`patchFormFromBlock() this.form should not be null when this function is called`);
    }
  }

  protected override getUpdatedBlock(): BlockLifeAct {
    if (this.form && this.block) {
      const control: AbstractControl | null = this.form.get(FormControlName.lifeact);
      if (control) {
        const updatedBlock: BlockLifeAct = {
          ...this.block,
          data: {
            ...this.block.data,
            value: control.value.note,
            lifeActCompleted: control.value.completed ? '1' : '0',
          },
        };

        this.updateBackEndWithUpdatedLifeChanges(updatedBlock);

        return {
          ...this.block,
          data: {
            ...this.block.data,
            value: control.value.note,
            lifeActCompleted: control.value.completed ? '1' : '0',
          },
        };
      }
    }

    throw new Error(`getUpdatedBlock() this.form or control should not be null when this function is called`);
  }

  private updateBackEndWithUpdatedLifeChanges(updatedBlock: BlockLifeAct): void {
    const programDataCodesToSave: ProgramDataCode[] = [];

    if (typeof updatedBlock.data.value === 'string') {
      programDataCodesToSave.push({
        code: updatedBlock.data.program_data_code,
        value: updatedBlock.data.value || '',
      });
    }

    if (updatedBlock.data.lifeActCompleted) {
      programDataCodesToSave.push({
        code: updatedBlock.data.program_data_code_completed,
        value: updatedBlock.data.lifeActCompleted,
      });
    }

    if (programDataCodesToSave.length > 0) {
      this.courseContentService.saveProgramDataCodesAndUpdateStep(
        ApiUrlProgramData.loggedIn,
        programDataCodesToSave,
        updatedBlock,
      );
    }
  }
}
